import {
  countSkippedByReason,
  SKIP_REASON_LABELS,
  type MoveToSetSelection,
  type MoveToSetSkipReason,
  type ParsedMoveToSetCsv,
} from "./parseMoveToSetCsv.js";
import { countByType } from "./useMoveToSetBatch.js";
import styles from "../PipelineBrowser.module.css";

const DISPLAY_CAP = 50;

const FORMAT_LABELS: Record<ParsedMoveToSetCsv["format"], string> = {
  simple: "testimonial_id,testimonial_type",
  worklist: "worklist export",
};

type MoveToSetParsedSummaryProps = {
  fileName: string;
  parsed: ParsedMoveToSetCsv;
  selection: MoveToSetSelection;
  includeNoCompanyId: boolean;
  onIncludeNoCompanyIdChange: (include: boolean) => void;
  disabled: boolean;
};

export function MoveToSetParsedSummary({
  fileName,
  parsed,
  selection,
  includeNoCompanyId,
  onIncludeNoCompanyIdChange,
  disabled,
}: MoveToSetParsedSummaryProps) {
  const byType = countByType(selection.rows);
  const skippedCounts = countSkippedByReason(selection.skipped);
  const skippedByReason = (Object.keys(SKIP_REASON_LABELS) as MoveToSetSkipReason[]).flatMap(
    reason => (skippedCounts[reason] ? [{ reason, count: skippedCounts[reason] }] : []),
  );
  const problems = [
    ...parsed.invalid.map(row => ({
      key: `line-${row.line}`,
      label: `Line ${row.line}, ${row.testimonialId || "(empty)"}: ${row.reason}`,
    })),
    ...parsed.duplicates.map(id => ({ key: `dup-${id}`, label: `${id} appears more than once` })),
  ];
  const shown = problems.slice(0, DISPLAY_CAP);
  const hidden = problems.length - shown.length;

  return (
    <>
      <div className={styles.syncStateMessage}>
        {fileName} ({FORMAT_LABELS[parsed.format]}): <strong>{selection.rows.length}</strong> rows
        to move, <strong>{selection.skipped.length}</strong> skipped,{" "}
        <strong>{parsed.invalid.length}</strong> invalid,{" "}
        <strong>{parsed.duplicates.length}</strong> duplicates
      </div>
      <div className={styles.syncStateMessage}>
        To set_testimonial: <strong>{byType.set_testimonial}</strong>. To clerk_testimonial:{" "}
        <strong>{byType.clerk_testimonial}</strong>.
      </div>
      {skippedByReason.length > 0 && (
        <ul className={styles.problemList} aria-label="Skipped rows by reason">
          {skippedByReason.map(({ reason, count }) => (
            <li key={reason}>
              Skipped, {SKIP_REASON_LABELS[reason]}: {count}
            </li>
          ))}
        </ul>
      )}
      {parsed.format === "worklist" && (
        <div className={styles.syncStateRow}>
          <label>
            <input
              type="checkbox"
              checked={includeNoCompanyId}
              disabled={disabled}
              onChange={event => onIncludeNoCompanyIdChange(event.target.checked)}
            />{" "}
            Include rows whose set has no company_id
          </label>
          <span className={styles.mappingHint}>
            {parsed.noCompanyIdRows.length} such rows in this file
          </span>
        </div>
      )}
      {parsed.duplicates.length > 0 && (
        <div className={styles.mappingError}>
          Duplicate ids must be removed before this file can be run.
        </div>
      )}
      {problems.length > 0 && (
        <>
          <ul className={styles.problemList} aria-label="Invalid and duplicate rows">
            {shown.map(problem => (
              <li key={problem.key}>{problem.label}</li>
            ))}
          </ul>
          {hidden > 0 && <div className={styles.syncStateMessage}>…and {hidden} more.</div>}
        </>
      )}
    </>
  );
}
